import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'

import List from '../List'
import Chip from '../../../components/Chip'
import ItemsLoading from '../../../components/Loaders/ItemsLoading'
import SkeletonListRow from '../List/Skeleton'
import ActionsDropdown from '../ActionsDropdown'
import PageSection from '../PageSection'
import ProviderColumn from './Columns/ProviderColumn'
import Checkbox from '../../../components/Form/Checkbox'
import NameCell from './Cells/NameCell'

import { getStatusTagColor } from '../../helpers/componentsHelpers'
import { insertIf } from '../../../helpers/common'
import { useReusableInsightsColumns } from './Columns/useReusableInsightsColumns'

import { formatListItemValues } from '../../formatters'

import { listItemsTableColumnsMaxWidth, listItemsTableColumnsMinWidth } from './columnsWidth'

import useStyles from './styles'

const ListItemsTable = ({
  items = [],
  errorColumns,
  platform,
  currencySymbol,
  isLoadingSelector,
  errorSelector,
  wasLoadedSelector,
  additionalDataIsLoading,
  showItemPlatformColumn = true,
  createItemLinkHelper,
  createItemLinkExtraParams,
  getActionsDropdownOptions,
  itemUpdatingId,
  itemDeletingId,
  noDataText,
  nameColumnLabel,
  itemsLoadingProps,
  loadMoreDataHandler,
  ContentWrapper = PageSection,
  showGoal,
  getShowGoal,
  listItemType,
  showRowCheckbox,
  getShowRowCheckbox,
  onRowCheck,
  useConversionResultsField,
  children,
  showInsightsColumns = true
}) => {
  const classes = useStyles()

  const [checkedRows, setCheckedRows] = useState({})

  const formattedItems = useMemo(
    () =>
      items.map(item => {
        if (item.error) {
          // handle cases when the item has some error for example token expiry error for some of the provider
          // in such cases the errorColumns will be rendered instead of itemsListColumns
          return {
            ...item,
            customCols: errorColumns
          }
        } else {
          return {
            // in come cases we need to have not formatted data e.g. calculate cpa manually,
            // for this case we store original item in data
            data: item,
            ...item,
            ...formatListItemValues(item, { symbol: currencySymbol })
          }
        }
      }),
    [items, errorColumns, currencySymbol]
  )

  const insightColumns = useReusableInsightsColumns({ platform, items, useConversionResultsField })

  const checkRowHandler = useCallback(
    e => {
      const { name } = e.target
      setCheckedRows(prevState => {
        const updatedState = {
          ...prevState,
          [name]: !prevState[name]
        }

        if (onRowCheck) {
          onRowCheck(updatedState)
        }

        return updatedState
      })
    },
    [onRowCheck]
  )

  const itemsListColumns = useMemo(
    () => [
      ...insertIf(showRowCheckbox, {
        Component: ({ id, provider }) =>
          getShowRowCheckbox ? (
            getShowRowCheckbox(provider) ? (
              <Checkbox name={id} id={id} checked={checkedRows[id]} onCheck={checkRowHandler} />
            ) : (
              ''
            )
          ) : (
            <Checkbox name={id} id={id} checked={checkedRows[id]} onCheck={checkRowHandler} />
          ),
        style: {
          maxWidth: listItemsTableColumnsMaxWidth.checkbox
        }
      }),
      ...insertIf(showItemPlatformColumn, {
        Component: ({ provider }) => <ProviderColumn provider={provider} />,
        style: {
          minWidth: listItemsTableColumnsMinWidth.platform,
          maxWidth: listItemsTableColumnsMinWidth.platform,
          paddingLeft: 5,
          paddingRight: 5
        }
      }),
      {
        headTitle: nameColumnLabel || 'Name',
        Component: item => (
          <NameCell
            item={item}
            createItemLinkHelper={createItemLinkHelper}
            createItemLinkExtraParams={createItemLinkExtraParams}
            showGoal={showGoal}
            getShowGoal={getShowGoal}
            listItemType={listItemType}
          />
        ),
        style: {
          minWidth: listItemsTableColumnsMinWidth.name,
          maxWidth: listItemsTableColumnsMaxWidth.name,
          // Resetting the whiteSpace to default value to allow multiline text
          whiteSpace: 'unset'
        }
      },
      ...(showInsightsColumns ? insightColumns : []),
      {
        Component: ({ status }) => {
          return <Chip className={classes.statusChip} text={status} color={getStatusTagColor(status)} />
        },
        style: {
          minWidth: listItemsTableColumnsMinWidth.status,
          maxWidth: listItemsTableColumnsMaxWidth.status,
          textOverflow: 'unset',
          marginLeft: 'auto'
        }
      },
      // if actions menu is empty then hide it
      ...insertIf(!!getActionsDropdownOptions && !!getActionsDropdownOptions({}).length, {
        Component: item => <ActionsDropdown options={getActionsDropdownOptions(item)} />,
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      })
    ],
    [
      showRowCheckbox,
      showItemPlatformColumn,
      nameColumnLabel,
      showInsightsColumns,
      insightColumns,
      getActionsDropdownOptions,
      getShowRowCheckbox,
      checkedRows,
      checkRowHandler,
      createItemLinkHelper,
      createItemLinkExtraParams,
      showGoal,
      getShowGoal,
      listItemType,
      classes.statusChip
    ]
  )

  const skeletonProps = useMemo(() => ({ cols: itemsListColumns }), [itemsListColumns])

  return (
    <ItemsLoading
      className={classes.list}
      itemsLength={items && items.length}
      SkeletonComponent={SkeletonListRow}
      skeletonProps={skeletonProps}
      errorSelector={errorSelector}
      isLoadingSelector={isLoadingSelector}
      wasLoadedSelector={wasLoadedSelector}
      additionalDataIsLoading={additionalDataIsLoading}
      noDataText={noDataText}
      ContentWrapper={ContentWrapper}
      loadMore={loadMoreDataHandler}
      {...itemsLoadingProps}
    >
      <List
        cols={itemsListColumns}
        listData={formattedItems}
        itemUpdatingId={itemUpdatingId}
        itemDeletingId={itemDeletingId}
        Skeleton={<SkeletonListRow {...skeletonProps} key={uuidv4()} />}
        isBigRow
      />
      {children}
    </ItemsLoading>
  )
}

ListItemsTable.propTypes = {
  items: PropTypes.array.isRequired,
  platform: PropTypes.string,
  currencySymbol: PropTypes.string,
  isLoadingSelector: PropTypes.func.isRequired,
  errorSelector: PropTypes.func.isRequired,
  wasLoadedSelector: PropTypes.func.isRequired,
  additionalDataIsLoading: PropTypes.bool,
  showItemPlatformColumn: PropTypes.bool,
  createItemLinkHelper: PropTypes.func,
  createItemLinkExtraParams: PropTypes.object,
  getActionsDropdownOptions: PropTypes.func,
  itemUpdatingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  itemDeletingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noDataText: PropTypes.string,
  nameColumnLabel: PropTypes.string,
  itemsLoadingProps: PropTypes.object,
  loadMoreDataHandler: PropTypes.func,
  ContentWrapper: PropTypes.elementType
}

export default ListItemsTable
